import Head from 'next/head';
import { jsonLdScriptProps } from 'react-schemaorg';
import { ItemList } from 'schema-dts';
import type { GetServerSidePropsContext, NextPage } from 'next';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { isMaintenance, set503Header, setDefaultCacheHeader, setNoCacheHeader, ssrPersistedQuery, transformSeoToMetas } from '../../../helpers/SSRHelpers';
import i18next, { initWithLangAndLabels } from '../../../i18n';
import { Tag, TagOrderBy } from '../../../graphql/generated/api-graphql';
import { Configuration, Tag as TagService } from '../../../graphql/Services';
import TagPageTemplate, { TagPageTemplateProps } from '../../../templates/TagPageTemplate';
import Router from '../../../routes/Router';
import { wediaImagesTransform } from '../../../relay/Common/wedia';
import { transformTagCard } from '../../../relay/Tag/transformTagCard';
import { getTagCardsCount, resolveTagItemType, TagType, TagTypeArray } from '../../../relay/Tag/TagType';
import type { GetServerSidePropsReturnType, PageProps } from '../../../relay/Common/PageProps';
import { transformTagForHero } from '../../../relay/Common/tag';
import { getTagUrl } from '../../../relay/Tag/helpers';
import { TAG_PER_PAGE } from '../../../relay/Tag/const';
import BugsnagHelpers from '../../../helpers/BugsnagHelpers';
import { CardsBlockProps } from '../../../components/molecules/CardsBlock';
import { jsonLdTagCardsList } from '../../../relay/Tag/tag';
import tracking, { TrackingContentGroup } from '../../../tracking';
const HeaderWrapper = dynamic(() => import('../../../relay/Header/HeaderWrapper'));
const FooterWrapper = dynamic(() => import('../../../relay/Footer/FooterWrapper'));
type TagPageProps = PageProps & {
  data: Tag;
  initialSlug: string;
  initialType: TagType;
  initialPage: number;
};
const TagPage: NextPage<TagPageProps> = ({
  configuration,
  data,
  initialSlug,
  initialType,
  initialPage
}) => {
  const {
    t
  } = useTranslation();
  const slug: string = initialSlug ?? '';
  const pageNumber: number = initialPage ?? 1;
  const type: TagType = initialType ?? TagType.all;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    tracking.page({
      brand: undefined,
      group: TrackingContentGroup.TAG,
      page: data?.name,
      section: undefined,
      hideRubrique: true
    });
  }, [data?.name]);
  const cardsBlockProps: CardsBlockProps = {
    cards: data?.items?.data?.map(card => transformTagCard(t, card))
  };
  const jsonLdCardsList = cardsBlockProps.cards ? jsonLdTagCardsList(data?.name, cardsBlockProps.cards) : undefined;
  const props: TagPageTemplateProps = {
    header: <HeaderWrapper data={configuration?.header} socials={configuration?.footer?.socialLinks} />,
    footer: <FooterWrapper data={configuration?.footer} />,
    title: t(`tagPage_type_${type}_title`),
    subtitle: `“${data?.name}”`,
    heroBannerProps: {
      listTagLinksProps: {
        list: TagTypeArray.map(id => ({
          linkProps: {
            text: t(`tagPage_type_${id}_label`),
            scroll: false,
            onClick: () => {
              Router.push(getTagUrl({
                slug,
                ...(id !== TagType.all && {
                  type: id
                })
              }), undefined, {
                scroll: false
              });
              ref?.current?.scrollIntoView({
                behavior: 'smooth'
              });
            }
          },
          count: getTagCardsCount(data?.items?.count, id),
          active: type === id
        }))
      },
      bannerProps: {
        imageProps: {
          alt: data?.name,
          maxWidth: 1280,
          width: 1280,
          withHD: true,
          withLazyLoading: false,
          images: wediaImagesTransform(data?.picture)
        },
        text: data?.name
      }
    },
    mainContentRef: ref,
    cardsBlockProps,
    paginationProps: {
      pageCount: data?.items?.paginatorInfo?.lastPage ?? 1,
      pageRangeDisplayed: 2,
      marginPagesDisplayed: 2,
      forcePage: (data?.items?.paginatorInfo?.currentPage ?? 1) - 1,
      hrefBuilder: pageIndex => {
        return getTagUrl({
          slug,
          page: pageIndex + '',
          ...(type !== TagType.all && {
            type
          })
        });
      },
      onPageChange: e => {
        if (e.selected + 1 !== pageNumber) {
          Router.push(getTagUrl({
            page: e.selected + 1 + '',
            slug,
            ...(type !== TagType.all && {
              type
            })
          }), undefined, {
            scroll: false
          });
          ref?.current?.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }
    },
    tagsCloudProps: {
      title: t('tagPage_related_title'),
      tags: data?.relatedTags?.map(el => transformTagForHero(el, data?.name))
    },
    ...(data?.longDescription && {
      htmlProps: {
        text: data?.longDescription
      }
    }),
    ...(data?.seoBlockTitle && data?.seoBlockBody && {
      seoProps: {
        text: `<h3>${data?.seoBlockTitle}</h3><p>${data?.seoBlockBody}<p>`
      }
    })
  };
  return <>
      {jsonLdCardsList && <Head>
          <script {...jsonLdScriptProps<ItemList>({
        '@context': 'https://schema.org',
        ...jsonLdCardsList
      })} />
        </Head>}
      <TagPageTemplate {...props} />
    </>;
};
export default TagPage;
export async function getServerSideProps(context: GetServerSidePropsContext): GetServerSidePropsReturnType<TagPageProps> {
  try {
    const {
      slug,
      page,
      type
    } = context.params ?? {};
    const pageNumber = isNaN(parseInt(page as string, 10)) ? 1 : parseInt(page as string, 10);
    if (type && !Object.values(TagType).includes(type as TagType)) {
      return {
        redirect: {
          destination: getTagUrl({
            slug
          }),
          permanent: true
        }
      };
    }
    if (page === '1') {
      return {
        redirect: {
          destination: getTagUrl({
            slug,
            type
          }),
          permanent: true
        }
      };
    }
    const [configurationRs, rs] = await Promise.all([ssrPersistedQuery(Configuration.queries.configuration), ssrPersistedQuery(TagService.queries.tag, {
      slug,
      page: pageNumber,
      type: resolveTagItemType(type as TagType),
      first: TAG_PER_PAGE,
      orderBy: TagOrderBy.Desc
    })]);
    const i18n = initWithLangAndLabels('fr', {});
    const configuration = configurationRs?.data ?? null;
    if (!configuration || rs?.errors) {
      set503Header(context);
      return {
        props: {
          i18n,
          error: true
        }
      };
    }
    if (isMaintenance(configuration)) {
      set503Header(context);
    } else {
      setDefaultCacheHeader(context);
    }
    if (!rs?.data) {
      return {
        notFound: true,
        props: {
          i18n,
          configuration
        }
      };
    }
    return {
      props: {
        i18n,
        configuration,
        metas: transformSeoToMetas(getTagUrl({
          page,
          slug,
          type
        }), {
          ...rs?.data?.seo,
          title: rs?.data?.seo?.title ?? i18next.t('tagPage_seo_title', {
            tag: rs?.data?.name
          }),
          description: rs?.data?.seo?.description ?? i18next.t('tagPage_seo_description', {
            tag: rs?.data?.name
          })
        }),
        data: rs?.data ?? null,
        initialSlug: slug as string,
        initialPage: pageNumber,
        initialType: type as TagType ?? TagType.all
      }
    };
  } catch (e: any) {
    BugsnagHelpers?.notify(e, event => {
      const {
        resolvedUrl,
        query,
        params
      } = context;
      event.addMetadata('context', {
        resolvedUrl,
        query,
        params
      });
    });
    setNoCacheHeader(context);
    return {
      notFound: true
    };
  }
}